import { makeAutoObservable, reaction } from 'mobx';

import filterStore from './filter-store';
import globalAppStore from './global-app-store';

import { fetchBankLots, fetchLots, fetchMausoleumBanks } from 'src/api';
import { CURRENCY_CENTS_COUNT, IS_MAUSOLEUM_VIEW, LotStatus } from 'src/constants';
import type { ILatLngCoords, ILot, IOption } from 'src/interfaces';

class LotsViewStore {
  bankLots: ILot[] = [];
  bankOptions: IOption[] = [];
  isLoading = true;
  lots: ILot[] = [];
  selectedLot: ILot | null = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });

    reaction(
      () => [filterStore.apiParams, filterStore.isLoading],
      () => this.loadLots()
    );

    reaction(
      () => this.visibleMapLots,
      () => this.checkSelectedLot()
    );

    reaction(
      () => filterStore.selectedBank,
      (selectedBank) => {
        if (IS_MAUSOLEUM_VIEW && selectedBank) {
          this.loadBankLots(selectedBank);
        } else {
          this.setBankLots([]);
        }
      }
    );

    reaction(
      () => globalAppStore.cemetery?.mausoleums?.[0]?.id,
      (mausoleumId) => {
        if (IS_MAUSOLEUM_VIEW && mausoleumId) {
          void this.loadBankOptions(mausoleumId);
        }
      },
      { fireImmediately: true }
    );
  }

  private setLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  private setBankLots(lots: ILot[]) {
    this.bankLots = lots;
  }

  private setBankOptions(options: IOption[]) {
    this.bankOptions = options;
  }

  private setLots(lots: ILot[]) {
    this.lots = lots;
  }

  setSelectedLot(lot: ILot | null) {
    // set lot copy to enable lot back navigation if it's already selected
    this.selectedLot = lot ? { ...lot } : null;
  }

  resetSelectedLot() {
    this.setSelectedLot(null);
  }

  private checkSelectedLot() {
    if (!this.selectedLot) {
      return;
    }

    if (!this.visibleMapLots.find((item) => item.id === this.selectedLot!.id)) {
      this.resetSelectedLot();
    }
  }

  get visibleListLots() {
    return IS_MAUSOLEUM_VIEW
      ? this.visibleMapLots.filter((lot) => lot.status === LotStatus.AVAILABLE_FOR_SALE)
      : this.visibleMapLots;
  }

  get filteredBankLots() {
    const { priceRange, rights, type } = filterStore;

    return this.bankLots.filter((lot) => {
      const isTypeMatched = !type || lot.type === type;
      const isRightsMatched = !rights || lot.rights === rights;
      const isPriceMatched =
        !priceRange ||
        (lot.price >= priceRange.from * CURRENCY_CENTS_COUNT &&
          lot.price <= priceRange.to * CURRENCY_CENTS_COUNT);

      return isTypeMatched && isRightsMatched && isPriceMatched;
    });
  }

  get visibleMapLots() {
    return IS_MAUSOLEUM_VIEW && filterStore.selectedBank ? this.filteredBankLots : this.lots;
  }

  get inGroundLots() {
    return this.lots.filter((item) => !item.mausoleum);
  }

  get mausoleumLots() {
    return this.lots.filter((item) => !!item.mausoleum);
  }

  get mausoleumCoords() {
    const coords: any = {};

    this.mausoleumLots.forEach((item) => {
      if (!coords[item.mausoleum!.id]) {
        coords[item.mausoleum!.id] = {
          lat: item.mausoleum?.location.coordinates[1],
          lng: item.mausoleum?.location.coordinates[0],
        };
      }
    });

    return Object.values(coords) as ILatLngCoords[];
  }

  async loadBankLots(bankId: string) {
    this.setLoading(true);
    this.setBankLots([]);
    const lots = await fetchBankLots(bankId);
    this.setBankLots(lots || []);
    this.setLoading(false);
  }

  async loadBankOptions(mausoleumId: string) {
    const banks = await fetchMausoleumBanks(mausoleumId);
    this.setBankOptions(banks?.map(({ id, name }) => ({ id, value: name })) ?? []);
  }

  async loadLots() {
    this.setLoading(true);
    const lots = await fetchLots(filterStore.apiParams);
    this.setLots(lots || []);
    this.setLoading(false);
  }
}

export default LotsViewStore;
